'use client';

import {
  FeedbackInviteMode,
  FeedbackStatus,
  FeedbackInviteStatus,
  FeedbackSource,
  FeedbackInviteRecipientStatus,
} from '@up2stay/up2promo-api-client';
import { Badge, cn } from '@up2stay/up2promo-ui';
import { Star } from 'lucide-react';

import { useDictionary } from '@/core/providers/dictionary-provider';

export function FeedbackStars({
  stars,
  className,
}: {
  stars: number;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(5, Math.round(stars)));
  return (
    <span
      className={cn('inline-flex items-center gap-0.5', className)}
      aria-label={`${clamped} / 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'h-3.5 w-3.5',
            i < clamped
              ? 'fill-amber-500 text-amber-500'
              : 'text-foreground/25',
          )}
          aria-hidden
        />
      ))}
    </span>
  );
}

export function FeedbackStatusBadge({ status }: { status: FeedbackStatus }) {
  const labels = useDictionary().feedback.status;
  const label =
    status === FeedbackStatus.NEW
      ? labels.new
      : status === FeedbackStatus.SEEN
        ? labels.seen
        : labels.resolved;
  const variant =
    status === FeedbackStatus.NEW
      ? 'warning'
      : status === FeedbackStatus.SEEN
        ? 'info'
        : 'success';
  return <Badge variant={variant}>{label}</Badge>;
}

export function FeedbackSourceLabel({ source }: { source: FeedbackSource }) {
  const labels = useDictionary().feedback.source;
  if (source === FeedbackSource.INVITE) return labels.invite;
  if (source === FeedbackSource.PRODUCT) return labels.product;
  return labels.api;
}

export function FeedbackInviteStatusBadge({
  status,
}: {
  status: FeedbackInviteStatus;
}) {
  const labels = useDictionary().feedback.inviteStatus;
  const map: Record<FeedbackInviteStatus, string> = {
    [FeedbackInviteStatus.DRAFT]: labels.draft,
    [FeedbackInviteStatus.SCHEDULED]: labels.scheduled,
    [FeedbackInviteStatus.SENDING]: labels.sending,
    [FeedbackInviteStatus.ACTIVE]: labels.active,
    [FeedbackInviteStatus.COMPLETED]: labels.completed,
    [FeedbackInviteStatus.CANCELLED]: labels.cancelled,
  };
  const variant =
    status === FeedbackInviteStatus.CANCELLED
      ? 'outline'
      : status === FeedbackInviteStatus.COMPLETED
        ? 'success'
        : status === FeedbackInviteStatus.ACTIVE
          ? 'default'
          : status === FeedbackInviteStatus.SENDING
            ? 'warning'
            : status === FeedbackInviteStatus.SCHEDULED
              ? 'info'
              : 'outline';
  return <Badge variant={variant}>{map[status]}</Badge>;
}

export function FeedbackModeLabel({ mode }: { mode: FeedbackInviteMode }) {
  const labels = useDictionary().feedback.modes;
  if (mode === FeedbackInviteMode.FEEDBACK_ONLY) return labels.feedbackOnly;
  if (mode === FeedbackInviteMode.DIRECT_REVIEW) return labels.directReview;
  return labels.feedbackThenReview;
}

export function FeedbackRecipientStatusBadge({
  status,
}: {
  status: FeedbackInviteRecipientStatus;
}) {
  const labels = useDictionary().feedback.recipientStatus;
  const map: Record<FeedbackInviteRecipientStatus, string> = {
    [FeedbackInviteRecipientStatus.PENDING]: labels.pending,
    [FeedbackInviteRecipientStatus.SENT]: labels.sent,
    [FeedbackInviteRecipientStatus.OPENED]: labels.opened,
    [FeedbackInviteRecipientStatus.COMPLETED]: labels.completed,
    [FeedbackInviteRecipientStatus.BOUNCED]: labels.bounced,
    [FeedbackInviteRecipientStatus.UNSUBSCRIBED]: labels.unsubscribed,
    [FeedbackInviteRecipientStatus.EXPIRED]: labels.expired,
  };
  const variant =
    status === FeedbackInviteRecipientStatus.COMPLETED
      ? 'success'
      : status === FeedbackInviteRecipientStatus.BOUNCED
        ? 'destructive'
        : status === FeedbackInviteRecipientStatus.PENDING
          ? 'warning'
          : status === FeedbackInviteRecipientStatus.SENT ||
              status === FeedbackInviteRecipientStatus.OPENED
            ? 'info'
            : 'outline';
  return <Badge variant={variant}>{map[status]}</Badge>;
}
